import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Button,
  Chip,
  Pagination,
} from '@mui/material';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import CircleIcon from '@mui/icons-material/Circle';
import PageHeader from '../components/common/PageHeader';
import LoadingSpinner from '../components/common/LoadingSpinner';
import EmptyState from '../components/common/EmptyState';
import { NotificationContext } from '../contexts/NotificationContext';
import {
  getNotifications,
  deleteNotification,
} from '../services/notificationService';
import { useNavigate } from 'react-router-dom';

const getNotificationIcon = (type) => {
  switch (type) {
    case 'status_change':
      return <AssignmentOutlinedIcon color="primary" />;
    case 'assignment':
      return <NotificationsActiveOutlinedIcon color="secondary" />;
    case 'warning':
      return <WarningAmberOutlinedIcon color="warning" />;
    case 'success':
      return <CheckCircleOutlinedIcon color="success" />;
    default:
      return <InfoOutlinedIcon color="action" />;
  }
};

const formatTimestamp = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const NotificationsPage = () => {
  const navigate = useNavigate();
  const { markAsRead, markAllAsRead, fetchUnreadCount } = useContext(NotificationContext);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchAllNotifications = useCallback(async (pageNum) => {
    try {
      setLoading(true);
      const { data } = await getNotifications(pageNum);
      const result = data.data;
      setNotifications(result?.notifications || result || []);
      setTotalPages(result?.totalPages || 1);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllNotifications(page);
  }, [page, fetchAllNotifications]);

  const handleNotificationClick = async (notification) => {
    if (!notification.isRead) {
      await markAsRead(notification._id);
      setNotifications((prev) =>
        prev.map((n) => (n._id === notification._id ? { ...n, isRead: true } : n))
      );
    }
    if (notification.link) {
      navigate(notification.link);
    }
  };

  const handleDelete = async (e, notificationId) => {
    e.stopPropagation();
    try {
      await deleteNotification(notificationId);
      setNotifications((prev) => prev.filter((n) => n._id !== notificationId));
      fetchUnreadCount();
    } catch (err) {
      console.error('Failed to delete notification:', err);
    }
  };

  const handleMarkAllRead = async () => {
    await markAllAsRead();
    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
  };

  const handlePageChange = (_, newPage) => {
    setPage(newPage);
  };

  if (loading && notifications.length === 0) return <LoadingSpinner />;

  return (
    <Box>
      <PageHeader title="Notifications">
        <Button
          variant="outlined"
          size="small"
          onClick={handleMarkAllRead}
        >
          Mark All as Read
        </Button>
      </PageHeader>

      {notifications.length === 0 ? (
        <EmptyState message="No notifications" />
      ) : (
        <>
          <Paper>
            <List disablePadding>
              {notifications.map((notification) => (
                <ListItem
                  key={notification._id}
                  onClick={() => handleNotificationClick(notification)}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={(e) => handleDelete(e, notification._id)}
                      aria-label="delete notification"
                    >
                      <DeleteOutlinedIcon fontSize="small" />
                    </IconButton>
                  }
                  sx={{
                    cursor: 'pointer',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: notification.isRead ? 'transparent' : 'action.hover',
                    '&:hover': {
                      backgroundColor: 'action.selected',
                    },
                  }}
                >
                  <ListItemIcon>
                    {getNotificationIcon(notification.type)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {!notification.isRead && (
                          <CircleIcon sx={{ fontSize: 8, color: 'primary.main' }} />
                        )}
                        <Typography
                          variant="body2"
                          fontWeight={notification.isRead ? 400 : 600}
                        >
                          {notification.title}
                        </Typography>
                        {!notification.isRead && (
                          <Chip label="New" size="small" color="primary" sx={{ height: 20, fontSize: '0.65rem' }} />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                          {notification.message}
                        </Typography>
                        <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5 }} display="block">
                          {formatTimestamp(notification.createdAt)}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Paper>

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default NotificationsPage;
